import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  Badge,
  Button,
  Spinner,
  Tabs,
  Toggle,
  cn,
  useAuth,
  useToast,
} from "@toss-ads/ui";
import { DownloadHistoryView } from "../components/DownloadHistoryView";
import { UserDataTab } from "../components/UserDataTab";
import {
  getUser,
  impersonateUser,
  setUserDisabled,
  setUserNote,
  setUserColor,
  setUserPhone,
  setUserRole,
  setUserSmsOnLead,
} from "../lib/api";
import type { UserRole, UserRow } from "../lib/types";

type TabKey = "info" | "campaigns" | "downloads";

const TABS = [
  { key: "info", label: "기본 정보" },
  { key: "campaigns", label: "캠페인 & 데이터" },
  { key: "downloads", label: "다운로드 기록" },
];

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [tab, setTab] = React.useState<TabKey>("info");

  const {
    data: user,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["admin", "users", id],
    queryFn: () => getUser(id!),
    enabled: !!id,
  });

  const toggleDisabled = useMutation({
    mutationFn: () => {
      if (!user) throw new Error("user not loaded");
      return setUserDisabled(user.id, !user.disabled);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users", id] });
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success(
        user!.disabled
          ? "계정이 활성화되었습니다"
          : "계정이 비활성화되었습니다",
      );
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "변경 실패");
    },
  });

  function handleToggle() {
    if (!user) return;
    const willDeactivate = !user.disabled;
    if (me && user.id === me.id && willDeactivate) {
      toast.warn("자기 자신은 비활성화할 수 없습니다");
      return;
    }
    toggleDisabled.mutate();
  }

  const impersonate = useMutation({
    mutationFn: () => impersonateUser(id!),
    onSuccess: (data) => {
      const portalBase = (
        (import.meta.env.VITE_PORTAL_URL as string | undefined) ?? ""
      ).replace(/\/+$/, "");
      const url = `${portalBase}/impersonate?t=${encodeURIComponent(data.accessToken)}&actor=${encodeURIComponent(me?.email ?? "")}`;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "유저 진입 실패");
    },
  });

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Button variant="text" size="md" onClick={() => navigate("/users")}>
          ← 유저 목록
        </Button>
      </div>

      {isLoading && (
        <div className="rounded-lg border bg-surface py-16 flex justify-center text-ink-subtle">
          <Spinner size={20} />
        </div>
      )}

      {isError && (
        <div className="rounded-lg border bg-surface py-12 text-center">
          <p className="text-[13px] text-danger">
            {error instanceof ApiError
              ? error.message
              : "유저를 불러오지 못했습니다."}
          </p>
        </div>
      )}

      {user && (
        <>
          <div className="mb-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-[18px] font-semibold tracking-tight text-ink">
                  {user.email}
                </h1>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <Badge
                    variant={
                      user.role === "developer"
                        ? "primary"
                        : user.role === "admin"
                          ? "warning"
                          : "neutral"
                    }
                  >
                    {user.role}
                  </Badge>
                  <Badge variant={user.disabled ? "neutral" : "success"} dot>
                    {user.disabled ? "비활성" : "활성"}
                  </Badge>
                  <span className="text-[12px] text-ink-subtle">
                    생성 {formatDate(user.createdAt)}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {user.role === "user" &&
                  !user.disabled &&
                  me?.id !== user.id && (
                    <Button
                      variant="outline"
                      size="md"
                      loading={impersonate.isPending}
                      onClick={() => impersonate.mutate()}
                    >
                      유저 실제 페이지 확인
                    </Button>
                  )}
                <Button
                  variant={user.disabled ? "primary" : "outline"}
                  size="md"
                  loading={toggleDisabled.isPending}
                  onClick={handleToggle}
                >
                  {user.disabled ? "계정 활성화" : "계정 비활성화"}
                </Button>
              </div>
            </div>
          </div>

          <Tabs
            tabs={TABS}
            current={tab}
            onChange={(k) => setTab(k as TabKey)}
            className="mb-6"
          />

          {tab === "info" && (
            <BasicInfoTab user={user} isSelf={me?.id === user.id} />
          )}
          {tab === "campaigns" && <UserDataTab userId={user.id} />}
          {tab === "downloads" && (
            <DownloadHistoryView logs={user.downloadLogs ?? []} />
          )}
        </>
      )}
    </div>
  );
}

function BasicInfoTab({ user, isSelf }: { user: UserRow; isSelf: boolean }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { user: me } = useAuth();
  // developer 만 developer 권한을 다룰 수 있음. admin 은 부여·변경 불가.
  const canEditDeveloper = me?.role === "developer";
  const roleLocked = !canEditDeveloper && user.role === "developer";
  const [role, setRole] = React.useState<UserRole>(user.role);
  const [note, setNote] = React.useState<string>(user.note ?? "");
  const [color, setColor] = React.useState<string>(user.color ?? "");
  const [phone, setPhone] = React.useState<string>(user.phone ?? "");
  const [smsOnLead, setSmsOnLead] = React.useState<boolean>(user.smsOnLead);

  // 저장 성공(=user prop 갱신) 시 로컬 편집값을 서버값으로 재동기화.
  React.useEffect(() => {
    setRole(user.role);
    setNote(user.note ?? "");
    setColor(user.color ?? "");
    setPhone(user.phone ?? "");
    setSmsOnLead(user.smsOnLead);
  }, [user]);

  const roleDirty = role !== user.role;
  const noteDirty = note.trim() !== (user.note ?? "");
  const colorDirty = (color || null) !== (user.color ?? null);
  const phoneDirty = (phone.trim() || null) !== (user.phone ?? null);
  const smsDirty = smsOnLead !== user.smsOnLead;
  const anyDirty =
    roleDirty || noteDirty || colorDirty || phoneDirty || smsDirty;

  // 바뀐 필드만 각 세터로 한 번에 저장.
  const save = useMutation({
    mutationFn: async () => {
      const jobs: Array<Promise<unknown>> = [];
      if (roleDirty) jobs.push(setUserRole(user.id, role));
      if (noteDirty) jobs.push(setUserNote(user.id, note.trim() || null));
      if (colorDirty) jobs.push(setUserColor(user.id, color || null));
      if (phoneDirty) jobs.push(setUserPhone(user.id, phone.trim() || null));
      if (smsDirty) jobs.push(setUserSmsOnLead(user.id, smsOnLead));
      await Promise.all(jobs);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users", user.id] });
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success("저장되었습니다");
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "저장 실패"),
  });

  function handleCancel() {
    setRole(user.role);
    setNote(user.note ?? "");
    setColor(user.color ?? "");
    setPhone(user.phone ?? "");
    setSmsOnLead(user.smsOnLead);
  }

  return (
    <div className="rounded-lg border bg-surface p-7">
      <Row label="이메일" value={user.email} />
      <RowCustom label="권한">
        <div className="flex items-center gap-2">
          <select
            value={role}
            disabled={roleLocked}
            onChange={(e) => setRole(e.target.value as UserRole)}
            className="block h-[36px] px-2.5 text-[13px] rounded-base border border-border-strong bg-surface text-ink focus:outline-none focus:border-primary disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <option value="user">user</option>
            <option value="admin">admin</option>
            {(canEditDeveloper || user.role === "developer") && (
              <option value="developer">developer</option>
            )}
          </select>
          {roleLocked && (
            <span className="text-[11px] text-ink-subtle">
              developer 권한은 developer 만 변경할 수 있습니다.
            </span>
          )}
        </div>
      </RowCustom>
      <RowCustom label="메모">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="광고주명 / 담당자 등"
            maxLength={500}
            className="flex-1 h-[36px] px-2.5 text-[13px] rounded-base border border-border-strong bg-surface text-ink focus:outline-none focus:border-primary"
          />
        </div>
      </RowCustom>
      <RowCustom label="담당자 색">
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={color || "#2563eb"}
            onChange={(e) => setColor(e.target.value)}
            className="h-[36px] w-12 rounded-base border border-border-strong bg-surface p-0.5"
          />
          {color ? (
            <button
              type="button"
              className="text-[12px] text-ink-subtle hover:text-danger"
              onClick={() => setColor("")}
            >
              지우기
            </button>
          ) : (
            <span className="text-[12px] text-ink-subtle">미지정(기본색)</span>
          )}
        </div>
      </RowCustom>
      <RowCustom label="SMS 수신번호">
        <div className="flex items-center gap-2">
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="01012345678"
            maxLength={32}
            className="flex-1 h-[36px] px-2.5 text-[13px] rounded-base border border-border-strong bg-surface text-ink focus:outline-none focus:border-primary"
          />
        </div>
      </RowCustom>
      <RowCustom label="리드 SMS 알림">
        <div className="flex items-center gap-2">
          <Toggle checked={smsOnLead} onCheckedChange={setSmsOnLead} />
          <span className="text-[12px] text-ink-subtle">
            {smsOnLead
              ? "담당 광고주에 새 리드가 오면 문자 수신"
              : "수신 안 함"}
          </span>
          {phone.trim().length === 0 && smsOnLead && (
            <span className="text-[11px] text-warning">
              수신번호가 없어 발송되지 않습니다
            </span>
          )}
        </div>
      </RowCustom>

      <Row
        label="이메일 인증"
        value={
          user.emailVerifiedAt
            ? `${formatDateTime(user.emailVerifiedAt)} 인증 완료`
            : "미인증"
        }
      />
      <Row
        label="마지막 접속"
        value={
          user.lastSeenAt ? formatDateTime(user.lastSeenAt) : "접속 기록 없음"
        }
      />
      <Row label="생성일" value={formatDateTime(user.createdAt)} />
      <Row label="마지막 수정" value={formatDateTime(user.updatedAt)} />
      <Row label="상태" value={user.disabled ? "비활성" : "활성"} last />

      {anyDirty && (
        <div className="flex items-center justify-end gap-2 pt-4 mt-2 border-t border-border">
          <Button
            variant="text"
            size="md"
            disabled={save.isPending}
            onClick={handleCancel}
          >
            취소
          </Button>
          <Button
            variant="primary"
            size="md"
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            저장
          </Button>
        </div>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  last,
}: {
  label: string;
  value: string;
  last?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center py-3",
        !last && "border-b border-border",
      )}
    >
      <div className="w-32 text-[12px] text-ink-muted">{label}</div>
      <div className="text-[13px] text-ink">{value}</div>
    </div>
  );
}

function RowCustom({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center py-3 border-b border-border">
      <div className="w-32 text-[12px] text-ink-muted">{label}</div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return `${formatDate(iso)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}
